import React, { useEffect, useState, useCallback, forwardRef } from "react";
import { useIntl } from "react-intl";
import DatePicker from "react-datepicker";
import { FileText, Mail, DollarSign } from "react-feather";
import Tooltip from 'rc-tooltip';
import Skeleton from "../../components/Skeleton";
import Pagination from "../../components/Pagination";
import LinkUserToEnterpriseModal from './components/LinkUserToEnterpriseModal';
import ConfirmationResendEmailModal from "./components/ConfirmationResendEmailModal";
import AddBonusModal from "./components/AddBonusModal";
import Graph from './components/Graph';
import { api, getDashboardData } from "../../api";
import { debouncer } from "../../utils/debouncer";
import "react-datepicker/dist/react-datepicker.css";
import "./styles.scss";

const MODAL_LINK_USER = 'MODAL_LINK_USER';
const MODAL_CONFIRMATION_RESEND_EMAIL = 'MODAL_CONFIRMATION_RESEND_EMAIL';
const MODAL_ADD_BONUS = 'MODAL_ADD_BONUS';

const Dashboard = ({ setError }) => {
  const { formatMessage } = useIntl();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState({});
  const [graphData, setGraphData] = useState({});
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);
  const [userPage, setUserPage] = useState(1);
  const [userTotalPages, setUserTotalPages] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [modalSelectedUser, setModalSelectedUser] = useState({});
  const [selectedPessoa, setSelectedPessoa] = useState(null);

  const fetchUsersListing = debouncer((page, search = '') => {
    const url = 
      `/b3_estatisticas/users_listing?page=${page}&limit=10${search !== '' ? `&search=${search}` : ''}`

    api.get(url)
      .then(({ data: response }) => {
        const data = response.data;
        const users = data.results;

        const parseUsers = Object.keys(users)
          .map(user => users[user])
          .map(user => {
            user.data = user.data.sort((a, b) => {
              const aDate = new Date(a.data_transacao).getTime();
              const bDate = new Date(b.data_transacao).getTime();
              
              if (aDate < bDate) return 1;
              if (aDate > bDate) return -1;
              return 0;
            });

            return user;
          })

        setUsers(parseUsers)
        setUserTotalPages(data.totalPages);
      })
      .catch(() => {
        setError(formatMessage({ id: "erro-api" }));
      });
  }, 400);

  useEffect(() => {
    // fetchUsersListing(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboard = useCallback(() => {
    getDashboardData()
      .then(({ data: response }) => {
        if (response.acesso) {
          setResult(response.data);
          setGraphData(response.graphData)
          setLoading(false);
        } else {
          throw Error;
        }
      })
      .catch(() => {
        setError(formatMessage({ id: "erro-api" }));
        setLoading(false);
      });

    return () => {};
  }, [setError, formatMessage]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleResendEmail = useCallback(() => {
    const email = modalSelectedUser;

    api.get(`/b3_estatisticas/resend_user_email?email=${email}`)
      .then(response => {
        setOpenModal(null);
      })
      .catch(err => {
        setError(formatMessage({ id: 'erro-api' }))
        setOpenModal(null)
      })
  }, [formatMessage, modalSelectedUser, setError]); 

  const handleChangeDate = useCallback((min = null, max = null) => {
    const minimumDate = min || minDate;
    const maximumDate = max || maxDate;

    getDashboardData(
      minimumDate && minimumDate.getTime().toString().substr(0, 10), 
      maximumDate && maximumDate.getTime().toString().substr(0, 10)
    )
      .then(({ data: response }) => {
        setResult(response.data);
        setGraphData(response.graphData);
      })
      .catch(() => setError(formatMessage({ id: "erro-api" })))
      .finally(() => {
        setLoading(false);
      });
  }, [formatMessage, maxDate, minDate, setError]);

  return (
    <>
      <h1>Dashboard</h1>

      <div className="container-dash">
        {!loading && Object.keys(result).length === 0 ? (
          <div className="no-results">
            <FileText size="48px" />
            <p>{formatMessage({ id: "nenhuma-consulta-encontrada" })}</p>
          </div>
        ) : (
          <>
            {loading ? (
            <div className="dash-inner">
              <div className="dash-item"><Skeleton /></div>
              <div className="dash-item"><Skeleton /></div>
              <div className="dash-item"><Skeleton /></div>
              <div className="dash-item"><Skeleton /></div>
            </div>
            ) : (
              <>
                <div className="dash-inner">
                  <div className="dash-item">
                    <h2 id="usuarios_unicos">{result.usuarios_unicos.counter}</h2>
                    <span>{formatMessage({ id: 'usuarios-unicos' })}</span>
                  </div>

                  <div className="dash-item">
                    <h2 id="usuarios_com_consultas">{result.usuarios_com_consultas.counter}</h2>
                    <span>{formatMessage({ id: 'usuarios-consultas' })}</span>
                  </div>

                  <div className="dash-item">
                    <h2 id="consultas">{result.consultas_normais.counter + result.consultas_empresariais.counter}</h2>
                    <span>{formatMessage({ id: 'consultas' })}</span>
                  </div>

                  <div className="dash-item">
                    <h2 id="contas_antes">{result.contas_antes.counter}</h2>
                    <span>{formatMessage({ id: 'contas-antes-lancamento' })}</span>
                  </div>

                  <div className="dash-item">
                    <h2 id="contas_apos">{result.contas_apos.counter}</h2>
                    <span>{formatMessage({ id: 'contas-pos-lancamento' })}</span>
                  </div>
                </div>

                <div className="dash-date-filter">
                  <p>{formatMessage({ id: 'filtrar-por-data' })}</p>
                  <DatePicker 
                    selected={minDate}
                    maxDate={maxDate || new Date()}
                    dateFormat="dd/MM/yyyy"
                    customInput={<DatePickerCustom />}
                    onChange={date => {
                      setMinDate(date)
                      handleChangeDate(date, null)
                    }}
                  /> 

                  <span>{formatMessage({ id: 'ate' })}</span>

                  <DatePicker
                    selected={maxDate}
                    minDate={minDate}
                    maxDate={new Date()}
                    dateFormat="dd/MM/yyyy"
                    customInput={<DatePickerCustom />}
                    onChange={date => {
                      setMaxDate(date)
                      handleChangeDate(null, date)
                    }}
                  />
                </div>
              </>
            )}
          </>
        )}

        <div className="content-box">
          <h4>{formatMessage({ id: 'grafico' })}</h4>
          <Graph data={graphData} />
        </div>

        <div className="content-box">
          <h4>{formatMessage({ id: 'usuarios' })}</h4>
          
          <label htmlFor="search" className="search-user-box">
            Pesquise por nome ou e-mail
            <input 
              type="text" 
              name="search" 
              id="search" 
              placeholder="ex: José"
              onChange={e => fetchUsersListing(1, e.target.value)}
            />
          </label>

          <div className="users-table">
            <div className="users-table-header">
              <strong>{formatMessage({ id: 'nome' })}</strong>
              <strong>E-mail</strong>
              <strong>{formatMessage({ id: 'entrou-em' })}</strong>
            </div>

            <div className="users-table-body">
              {users.length > 0 ? users.map(user => (
                <div 
                  className="users-table-body-item"
                  key={user.username}
                  onClick={() => {
                    if (selectedUser === user.username) {
                      setSelectedUser('')
                    } else {
                      setSelectedUser(user.username)
                    }
                  }}
                >

                  <p className="name">
                    {user.ambiente.type === "empresarial" ? (
                      <Tooltip
                        placement="right"
                        trigger={["hover"]}
                        overlay={
                          <>
                            <p>Empresarial</p>
                          </>
                        }
                      >
                        <span className="label-enterprise">E</span>
                      </Tooltip>
                    ) : (
                      <span></span>
                    )}

                    {user.name && user.name}
                  </p>

                  <p className="email">{user.username && user.username}</p>
                  <p className="created">{user.created && user.created}</p>

                  <div className="actions">
                    <Tooltip
                      placement="right"
                      trigger={['hover']}
                      overlay={
                        <>
                          <p>Reenviar email de verificação</p>
                        </>
                      }
                    >
                      <button type="button" onClick={() => {
                        setModalSelectedUser(user.username);
                        setOpenModal(MODAL_CONFIRMATION_RESEND_EMAIL);
                      }}>
                        <Mail size={16} />
                      </button>
                    </Tooltip>
                    <Tooltip
                      placement="left"
                      overlay={
                        <>
                          <p>Vincular usuário à PJ</p>
                        </>
                      }
                    >
                      <button type="button" onClick={() => {
                        setModalSelectedUser(user.username);
                        setOpenModal(MODAL_LINK_USER);
                      }}>
                        PJ
                      </button>
                    </Tooltip>


                    {user.ambiente.type === "default" ? (
                      <Tooltip
                        placement="right"
                        trigger={["hover"]}
                        overlay={
                          <>
                            <p>Cadastrar Bônus</p>
                          </>
                        }
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedPessoa(user.pessoa_id);
                            setOpenModal(MODAL_ADD_BONUS);
                          }}
                        >
                          <DollarSign size={16} />
                        </button>
                      </Tooltip>
                    ) : (
                      <Tooltip
                        placement="right"
                        trigger={["hover"]}
                        overlay={
                          <>
                            <p>Cadastrar Bônus</p>
                          </>
                        }
                      >
                        <button
                          type="button"
                          className = "button-disabled"
                        >
                          <DollarSign size={16} />
                        </button>
                      </Tooltip>
                    )}

                  </div>

                  <div className={`
                    users-table-body-item-data
                    ${selectedUser === user.username ? 'show-item' : 'hide-item'}
                  `}>
                    <p><strong>{formatMessage({ id: 'numero-total-consultas' })}:</strong> {user.data ? user.data.length : 0}</p>
                    
                    {user.ambiente.type === "default" ? (
                      <p><strong>{formatMessage({ id: 'saldo' })}: </strong> R$ {user.balance ? user.balance : 0}</p>
                    ) : ( <span></span> ) }

                    <p>
                      <strong>{formatMessage({ id: 'primeira-consulta' })}: </strong> 
                      {user.data && user.data.length > 0 
                        ? user.data[0].data_transacao 
                        : ` ${formatMessage({ id: 'sem-consulta' })}`}
                    </p>
                    <p>
                      <strong>{formatMessage({ id: 'ultima-consulta' })}: </strong> 
                      {user.data && user.data.length > 0 
                        ? user.data[user.data.length - 1].data_transacao 
                        : ` ${formatMessage({ id: 'sem-consulta' })}`}
                    </p>
                  </div>
                </div>
              )) : (
                <div className="users-table-body-empty">
                  <h4>Não foram identificados usuários.</h4>
                </div>
              )}
            </div>
          </div>

          {users.length > 0 && 
            <Pagination 
              page={userPage} 
              totalPages={userTotalPages} 
              setPage={setUserPage} 
            />
          }
        </div>
      </div>

      {openModal === MODAL_CONFIRMATION_RESEND_EMAIL && (
        <ConfirmationResendEmailModal 
          setError={setError} 
          user={{
            email: modalSelectedUser
          }} 
          onConfirmation={handleResendEmail}
          onClose={() => {
            setOpenModal(null)
          }}
        /> 
      )}

      {openModal === MODAL_LINK_USER && (
        <LinkUserToEnterpriseModal 
          setError={setError} 
          data={modalSelectedUser} 
          onClose={() => {
            setOpenModal(null)
          }} 
        /> 
      )}

      {openModal === MODAL_ADD_BONUS && (
        <AddBonusModal 
          setError={setError} 
          user={{
            pessoa_id: selectedPessoa
          }} 
          onClose={() => {
            setOpenModal(null)
          }}
        /> 
      )}
    </>
  );
};

const DatePickerCustom = forwardRef((props, ref) => (
  <input className="date-picker-custom" {...props} ref={ref} />
));

export default Dashboard;
